import { doc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useCallback, useEffect, useRef, useState } from "react";
import { firestore, storage } from "../../firebase";
import { debugLogError } from "../../utils/debug-log-error";

export const PlaceholderImage = "https://placehold.co/150x150";
export const PlaceholderImageSmall = "https://placehold.co/72x72";

interface ProfileImageUpdateProps {
  userUid: string | null;
  profileImageUrl: string | null;
  onError: (error: Error) => void;
  onClearError: () => void;
}

const ProfileImageUpdate: React.FC<ProfileImageUpdateProps> = ({
  userUid,
  profileImageUrl,
  onError,
  onClearError,
}) => {
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleProfileImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        setProfileImageFile(file);
        onClearError();
      }
    },
    [onClearError]
  );

  const handleImageUpload = useCallback(async () => {
    if (!profileImageFile || !userUid) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    onClearError();

    const storageRef = ref(
      storage,
      `profileImages/${userUid}/${profileImageFile.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, profileImageFile);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Error uploading image:", error);
        onError(new Error("Failed to upload image. Please try again."));
        setIsUploading(false);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const profileDocRef = doc(firestore, "profiles", userUid);
          await updateDoc(profileDocRef, { photoURL: downloadURL });

          setIsUploading(false);
          setProfileImageFile(null);
        } catch (err) {
          debugLogError("ProfileImageUpdate::handleImageUpload", err);
          onError(
            new Error(
              "Failed to update profile with new image. Please try again."
            )
          );
          setIsUploading(false);
        }
      }
    );
  }, [onClearError, onError, profileImageFile, userUid]);

  useEffect(() => {
    // This effect triggers the upload when a new file is selected.
    if (profileImageFile && userUid && !isUploading) {
      handleImageUpload();
    }
  }, [profileImageFile, userUid, isUploading, handleImageUpload]);

  return (
    <div className="profile-image-section">
      <div
        className="profile-image-container"
        onClick={() => fileInputRef.current?.click()}
      >
        <img
          src={profileImageUrl || PlaceholderImage}
          alt="Profile"
          className="profile-image"
        />
        <span className="material-symbols-outlined edit-icon">edit</span>
      </div>
      <input
        type="file"
        accept="image/*"
        aria-label="profile image input"
        onChange={handleProfileImageSelect}
        disabled={isUploading}
        ref={fileInputRef}
        style={{ display: "none" }}
      />
      {isUploading && (
        <>
          <p>Uploading... {uploadProgress.toFixed(0)}%</p>
          <progress
            value={uploadProgress}
            max="100"
            style={{ width: "100%" }}
          />
        </>
      )}
    </div>
  );
};

export default ProfileImageUpdate;
